<template>
  <div class="home-container">
    <div class="m-16"></div>
    
    <div class="home-content">
      <h1 class="home-title">Risk Management Dashboard</h1>
      
      <div class="lists-container">
        <!-- Risk Maps Section -->
        <div class="list-section">
          <div class="section-header">
            <h2 class="section-title">Risk Maps</h2>
            <div class="section-count">{{ riskMaps.length }}</div>
          </div>
          <button class="create-button" @click="createRiskMap" :disabled="creatingRiskMap">
            {{ creatingRiskMap ? 'Creating...' : '+ Create risk map' }}
          </button>
          
          <div v-if="loadingRiskMaps" class="loading">Loading risk maps...</div>
          <div v-else-if="errorRiskMaps" class="error">{{ errorRiskMaps }}</div>
          <div v-else-if="riskMaps.length === 0" class="empty-state">No risk maps found</div>
          <div v-else class="list-items">
            <div 
              v-for="riskMap in riskMaps" 
              :key="riskMap.id" 
              class="list-item"
              @click="navigateToRiskMap(riskMap.id)"
            >
              <div class="item-content">
                <div class="item-title">Risk Map {{ riskMap.id.slice(0, 8) }}</div>
                <div class="item-meta">{{ riskMap.risks?.length || 0 }} risks</div>
              </div>
              <div class="item-arrow">→</div>
            </div>
          </div>
        </div>

        <!-- Stakeholders Section -->
        <div class="list-section">
          <div class="section-header">
            <h2 class="section-title">Stakeholders</h2>
            <div class="section-count">{{ stakeholders.length }}</div>
          </div>
          <button class="create-button" @click="createStakeholderMap" :disabled="creatingStakeholderMap">
            {{ creatingStakeholderMap ? 'Creating...' : '+ Create stakeholder map' }}
          </button>
          
          <div v-if="loadingStakeholders" class="loading">Loading stakeholders...</div>
          <div v-else-if="errorStakeholders" class="error">{{ errorStakeholders }}</div>
          <div v-else-if="stakeholders.length === 0" class="empty-state">No stakeholders found</div>
          <div v-else class="list-items">
            <div 
              v-for="stakeholder in stakeholders" 
              :key="stakeholder.id" 
              class="list-item"
              @click="navigateToStakeholderMap(stakeholder.id)"
            >
              <div class="item-content">
                <div class="item-title">Stakeholder Map {{ stakeholder.id.slice(0, 8) }}</div>
                <div class="item-meta">{{ stakeholder.categories?.length || 0 }} categories</div>
              </div>
              <div class="item-arrow">→</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { riskMapRequest } from './riskMap/services/RiskMapRequest.ts';
import { stakeholderMapRequest } from './stakeholdersMap/services/StakeholderMapRequest';
import { RiskMapReadResponse } from './riskMap/services/RiskMapResponses';
import { StakeholderMapResponse } from './stakeholdersMap/services/StakeholderMapResponse';

const router = useRouter();

const riskMaps = ref<RiskMapReadResponse[]>([]);
const stakeholders = ref<StakeholderMapResponse[]>([]);
const loadingRiskMaps = ref(false);
const loadingStakeholders = ref(false);
const errorRiskMaps = ref<string | null>(null);
const errorStakeholders = ref<string | null>(null);
const creatingRiskMap = ref(false);
const creatingStakeholderMap = ref(false);

const fetchRiskMaps = async () => {
  loadingRiskMaps.value = true;
  errorRiskMaps.value = null;
  try {
    riskMaps.value = await riskMapRequest.getAllRiskMaps();
  } catch (error) {
    errorRiskMaps.value = error instanceof Error ? error.message : 'Failed to load risk maps';
    console.error('Error fetching risk maps:', error);
  } finally {
    loadingRiskMaps.value = false;
  }
};

const fetchStakeholders = async () => {
  loadingStakeholders.value = true;
  errorStakeholders.value = null;
  try {
    stakeholders.value = await stakeholderMapRequest.getAllStakeholders();
  } catch (error) {
    errorStakeholders.value = error instanceof Error ? error.message : 'Failed to load stakeholders';
    console.error('Error fetching stakeholders:', error);
  } finally {
    loadingStakeholders.value = false;
  }
};

const navigateToRiskMap = (id: string) => {
  router.push({ path: '/risk-map', query: { id } });
};

const navigateToStakeholderMap = (id: string) => {
  router.push({ path: '/stakeholder-map', query: { id } });
};

const createRiskMap = async () => {
  creatingRiskMap.value = true;
  errorRiskMaps.value = null;
  try {
    const newRiskMap = await riskMapRequest.createRiskMap();
    await fetchRiskMaps();
    navigateToRiskMap(newRiskMap.id);
  } catch (error) {
    errorRiskMaps.value = error instanceof Error ? error.message : 'Failed to create risk map';
    console.error('Error creating risk map:', error);
  } finally {
    creatingRiskMap.value = false;
  }
};

const createStakeholderMap = async () => {
  creatingStakeholderMap.value = true;
  errorStakeholders.value = null;
  try {
    const newStakeholderMap = await stakeholderMapRequest.createStakeholderMap();
    await fetchStakeholders();
    navigateToStakeholderMap(newStakeholderMap.id);
  } catch (error) {
    errorStakeholders.value = error instanceof Error ? error.message : 'Failed to create stakeholder map';
    console.error('Error creating stakeholder map:', error);
  } finally {
    creatingStakeholderMap.value = false;
  }
};

onMounted(() => {
  fetchRiskMaps();
  fetchStakeholders();
});
</script>

<style scoped>
.home-container {
  background-color: #131827;
  color: white;
  min-height: 100vh;
  padding: 20px;
  font-family: Arial, sans-serif;
}

.home-content {
  max-width: 1200px;
  margin: 0 auto;
}

.home-title {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 40px;
  text-align: center;
}

.lists-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.list-section {
  background-color: #333;
  border-radius: 8px;
  padding: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #555;
}

.section-title {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
}

.section-count {
  background-color: #4caf50;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: bold;
}

.create-button {
  width: 100%;
  padding: 12px 20px;
  margin-bottom: 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.create-button:hover:not(:disabled) {
  background-color: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
}

.create-button:disabled {
  background-color: #666;
  cursor: not-allowed;
  opacity: 0.7;
}

.list-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.list-item {
  background-color: #444;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.list-item:hover {
  background-color: #555;
  border-color: #4caf50;
  transform: translateX(4px);
}

.item-content {
  flex: 1;
}

.item-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.item-meta {
  font-size: 14px;
  color: #aaa;
}

.item-arrow {
  font-size: 24px;
  color: #4caf50;
  margin-left: 16px;
}

.loading,
.error,
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #aaa;
}

.error {
  color: #dc143c;
}

@media (max-width: 768px) {
  .lists-container {
    grid-template-columns: 1fr;
  }
}
</style>

